import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, Empty, Input, NavBar, Popup, ProgressBar, SearchBar, Toast } from 'antd-mobile';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import API_ROUTES from '../api';
import './index.css';

// 队员选择统一按照身份排序：常驻、非正式、离队、临时，方便上传时快速找到正式队员。
const getMemberTypeMeta = (tempMember) => {
    if (tempMember === null || tempMember === undefined || tempMember === '') return { label: '常驻', order: 0 };
    if (Number(tempMember) === 3) return { label: '非正式', order: 1 };
    if (Number(tempMember) === 4) return { label: '离队', order: 2 };
    if (Number(tempMember) === 1) return { label: '临时', order: 3 };
    return { label: '其他', order: 4 };
};

const normalizeMemberRecords = (records) => {
    return (Array.isArray(records) ? records : [])
        .filter(item => item?.id)
        .map(item => {
            const name = item.name || item.memberName || item.userName || '队员';
            const typeMeta = getMemberTypeMeta(item.tempMember ?? item.temp_member);
            return {
                label: name,
                value: item.id,
                searchText: `${name} ${typeMeta.label}`,
                typeLabel: typeMeta.label,
                typeOrder: typeMeta.order
            };
        })
        .sort((a, b) => a.typeOrder - b.typeOrder || String(a.label || '').localeCompare(String(b.label || ''), 'zh-Hans-CN'));
};

export default function AlbumUpload() {
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef(null);
    const memberInfo = useMemo(() => JSON.parse(localStorage.getItem('memberInfo')), []);
    const fixedGameId = location.state?.gameId || '';
    const fixedGameName = location.state?.gameName || '';
    const fixedEventId = location.state?.eventId || '';
    const fixedEventName = location.state?.eventName || '';
    const fixedMemberId = location.state?.memberId || '';

    const [gameOptions, setGameOptions] = useState(fixedGameId ? [{ label: fixedGameName || '当前比赛', value: fixedGameId }] : [{ label: '不绑定比赛', value: '' }]);
    const [eventOptions, setEventOptions] = useState(fixedEventId ? [{ label: fixedEventName || '当前事件', value: fixedEventId }] : [{ label: '不绑定事件', value: '' }]);
    const [memberOptions, setMemberOptions] = useState([{ label: '不绑定队员', value: '' }]);
    const [gameId, setGameId] = useState(fixedGameId);
    const [eventId, setEventId] = useState(fixedEventId);
    const [bindMemberId, setBindMemberId] = useState(fixedMemberId);
    const [tagOptions, setTagOptions] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [remark, setRemark] = useState('');
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadLoadedBytes, setUploadLoadedBytes] = useState(0);
    const [uploadTotalBytes, setUploadTotalBytes] = useState(0);
    const [uploadStage, setUploadStage] = useState('');
    const [pickerType, setPickerType] = useState('');
    const [pickerSearch, setPickerSearch] = useState('');
    const [pickerDraftValue, setPickerDraftValue] = useState('');

    const formatGameDate = useCallback((item) => {
        const rawDate = item?.matchTime || item?.startDate || item?.matchDate || '';
        // 比赛下拉只保留日期，上传时选择场次更清晰。
        const matchedDate = String(rawDate).match(/\d{4}-\d{2}-\d{2}/);
        return matchedDate ? matchedDate[0] : '';
    }, []);

    const buildGameLabel = useCallback((item) => {
        const dateText = formatGameDate(item);
        return `${item.gameName || '比赛'}${dateText ? ` · ${dateText}` : ''}`;
    }, [formatGameDate]);

    const getGameOptions = useCallback(() => {
        if (fixedGameId) {
            // 从比赛详情进入上传页时，比赛已固定为当前场次，不再请求和展示比赛选择。
            setGameOptions([{ label: fixedGameName || '当前比赛', value: fixedGameId }]);
            return;
        }
        axios.get(API_ROUTES.album_dropdown_games).then((response) => {
            const records = response.data?.data || [];
            // 上传页只需要轻量比赛信息，避免拉取完整比赛列表导致下拉加载过慢。
            const options = records.map(item => ({
                label: buildGameLabel(item),
                value: item.id
            }));
            setGameOptions([{ label: '不绑定比赛', value: '' }, ...options]);
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '比赛列表加载失败' });
        });
    }, [buildGameLabel, fixedGameId, fixedGameName]);

    const eventDescriptions = useMemo(() => ({
        "1": "开始排位",
        "2": "比赛结束",
        "3": "运动战(其他)",
        "4": "乌龙球",
        "5": "黄牌",
        "6": "红牌",
        "7": "半场结束",
        "8": "半场开始",
        "9": "点球未进",
        "10": "半场开始",
        "11": "运动战(头球)",
        "12": "点球(右脚)",
        "13": "运动战(左脚)",
        "14": "运动战(右脚)",
        "15": "任意球(右脚)",
        "16": "角球(右脚)",
        "17": "上下场",
        "18": "暂停",
        "19": "任意球(左脚)",
        "20": "角球(左脚)",
        "21": "点球(左脚)"
    }), []);

    const formatEventTime = useCallback((value) => {
        if (value === null || value === undefined || value === '') return '时间未记';
        const seconds = Number(value);
        if (Number.isNaN(seconds)) return String(value);
        const minutes = Math.floor(seconds / 60);
        const remainSeconds = seconds % 60;
        return `${minutes}:${String(remainSeconds).padStart(2, '0')}`;
    }, []);

    const hasEventTime = useCallback((item) => {
        return !!item?.eventTimeName || !(item?.eventTime === null || item?.eventTime === undefined || item?.eventTime === '');
    }, []);

    const getEventTitle = useCallback((item) => {
        if (!item) return '比赛事件';
        return eventDescriptions[String(item.eventId)] || item.eventInfo || item.eventName || '比赛事件';
    }, [eventDescriptions]);

    const getEventKind = (item) => {
        const eid = String(item?.eventId);
        if (["3", "4", "9", "11", "12", "13", "14", "15", "16", "19", "20", "21"].includes(eid)) return 'goal';
        if (["5", "6"].includes(eid)) return 'card';
        if (["17"].includes(eid)) return 'sub';
        if (["1", "2", "7", "8", "10", "18"].includes(eid)) return 'status';
        return 'normal';
    };

    const isGoalOrCardEvent = useCallback((item) => {
        // 上传绑定比赛事件时只展示进球、点球、乌龙、黄红牌等关键事件。
        return ['goal', 'card'].includes(getEventKind(item));
    }, []);

    const isSectionStartEvent = useCallback((item) => {
        const eid = String(item?.eventId);
        return ["8", "10"].includes(eid) || getEventTitle(item) === '半场开始';
    }, [getEventTitle]);

    const buildEventOptionLabel = useCallback((item, index) => {
        const sectionText = item.sectionIndex ? `第${item.sectionIndex}节 ` : '';
        const timeText = hasEventTime(item) ? `${item.eventTimeName || formatEventTime(item.eventTime)} ` : '';
        const playerText = item.userName ? ` · ${item.userName}` : '';
        return `${index + 1}. ${sectionText}${timeText}${getEventTitle(item)}${playerText}`;
    }, [formatEventTime, getEventTitle, hasEventTime]);

    const getEventOptions = useCallback((nextGameId) => {
        axios.get(API_ROUTES.album_dropdown_events, {
            params: { gameId: nextGameId }
        }).then((response) => {
            const records = response.data?.data || [];
            // 先按记录时间还原事件流，再用半场开始节点推算节次；eventTime 每节会重置，不能直接作为全场排序依据。
            const sortedRecords = [...records].sort((a, b) => {
                const aEventTime = a.eventTime === null || a.eventTime === undefined || a.eventTime === '' ? null : Number(a.eventTime);
                const bEventTime = b.eventTime === null || b.eventTime === undefined || b.eventTime === '' ? null : Number(b.eventTime);
                const aCurrentTime = a.currentTime ? new Date(a.currentTime).getTime() : 0;
                const bCurrentTime = b.currentTime ? new Date(b.currentTime).getTime() : 0;
                if (aCurrentTime && bCurrentTime && aCurrentTime !== bCurrentTime) {
                    return aCurrentTime - bCurrentTime;
                }
                if (aEventTime !== null && bEventTime !== null && aEventTime !== bEventTime) {
                    return aEventTime - bEventTime;
                }
                return aCurrentTime - bCurrentTime;
            });
            let currentSection = 0;
            const keyEvents = sortedRecords.reduce((list, item) => {
                if (isSectionStartEvent(item)) {
                    currentSection += 1;
                }
                if (isGoalOrCardEvent(item)) {
                    list.push({
                        ...item,
                        sectionIndex: currentSection || 1
                    });
                }
                return list;
            }, []);
            // 绑定的是某一条比赛事件记录 id，不是事件类型 eventId。
            const options = keyEvents.map((item, index) => ({
                label: buildEventOptionLabel(item, index),
                value: item.id,
                raw: item,
                searchText: [
                    getEventTitle(item),
                    item.userName,
                    item.assistMemberName,
                    item.teamName
                ].filter(Boolean).join(' ')
            }));
            // 从赛况喜事入口进入时，事件可能不是进球/吃牌，也要保留当前绑定项供上传使用。
            const fixedOption = fixedEventId && !options.some(item => String(item.value) === String(fixedEventId))
                ? [{ label: fixedEventName || '当前事件', value: fixedEventId, searchText: fixedEventName || '当前事件' }]
                : [];
            setEventOptions([{ label: '不绑定事件', value: '' }, ...fixedOption, ...options]);
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '事件列表加载失败' });
            setEventOptions([{ label: '不绑定事件', value: '' }]);
        });
    }, [buildEventOptionLabel, fixedEventId, fixedEventName, getEventTitle, isGoalOrCardEvent, isSectionStartEvent]);

    const getMemberOptions = useCallback(() => {
        const yearNum = new Date().getFullYear();
        axios.get(API_ROUTES.soccer_list, {
            params: { year: yearNum, type: 1 }
        })
            .then((response) => {
                const data = response.data?.data || [];
                // 成员页接口返回分组结构，这里拍平成上传页的队员下拉选项。
                const records = Array.isArray(data) && data[0]?.items
                    ? data.flatMap(group => group.items || [])
                    : data;
                const options = normalizeMemberRecords(records);
                setMemberOptions([{ label: '不绑定队员', value: '' }, ...options]);
            })
            .catch(() => {
                Toast.show({ icon: 'fail', content: '队员列表加载失败' });
            });
    }, []);

    useEffect(() => {
        getGameOptions();
        getMemberOptions();
    }, [getGameOptions, getMemberOptions]);

    useEffect(() => {
        if (gameId) {
            getEventOptions(gameId);
        } else {
            setEventId('');
            setEventOptions([{ label: '不绑定事件', value: '' }]);
        }
    }, [gameId, getEventOptions]);

    const handleChooseFiles = (event) => {
        const nextFiles = Array.from(event.target.files || []);
        setFiles(nextFiles);
        setUploadProgress(0);
        setUploadLoadedBytes(0);
        setUploadTotalBytes(nextFiles.reduce((sum, file) => sum + (file.size || 0), 0));
        setUploadStage('');
    };

    const formatFileSize = (bytes) => {
        const size = Number(bytes || 0);
        if (size >= 1024 * 1024 * 1024) return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
        if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;
        if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${size} B`;
    };

    const getResponseETag = (headers = {}) => {
        return headers.etag || headers.ETag || headers.Etag || '';
    };

    const updateUploadProgress = (loadedBytes, totalBytes, stage) => {
        const nextTotal = totalBytes || files.reduce((sum, file) => sum + (file.size || 0), 0);
        const nextLoaded = Math.min(loadedBytes, nextTotal);
        const percent = nextTotal ? Math.min(100, Math.floor((nextLoaded / nextTotal) * 100)) : 0;
        setUploadLoadedBytes(nextLoaded);
        setUploadTotalBytes(nextTotal);
        setUploadProgress(percent);
        if (stage) {
            setUploadStage(stage);
        }
    };

    const abortMultipartUpload = async (uploadInfo) => {
        if (!uploadInfo?.uploadId || !uploadInfo?.objectKey) {
            return;
        }
        try {
            await axios.post(API_ROUTES.album_multipart_abort, {
                uploadId: uploadInfo.uploadId,
                objectKey: uploadInfo.objectKey
            });
        } catch (error) {
            console.warn('Abort album multipart upload failed', error);
        }
    };

    const uploadFileByMultipart = async (file, finishedBeforeBytes, totalBytes) => {
        let initData = null;
        try {
            setUploadStage(`初始化 ${file.name}`);
            const initResponse = await axios.post(API_ROUTES.album_multipart_init, {
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type || 'application/octet-stream'
            });

            if (initResponse.data?.code !== 200) {
                throw new Error(initResponse.data?.msg || '初始化分片上传失败');
            }

            initData = initResponse.data?.data;
            const parts = Array.isArray(initData?.parts) ? initData.parts : [];
            if (!initData?.uploadId || !initData?.objectKey || !parts.length) {
                throw new Error('初始化分片上传返回数据不完整');
            }

            let uploadedFileBytes = 0;
            const completedParts = [];

            for (const part of parts) {
                const chunk = file.slice(part.start, part.end);
                const uploadedBeforePart = uploadedFileBytes;
                setUploadStage(`上传 ${file.name} (${part.partNumber}/${initData.partCount || parts.length})`);

                const uploadResponse = await axios.put(part.uploadUrl, chunk, {
                    headers: { 'Content-Type': file.type || 'application/octet-stream' },
                    onUploadProgress: (event) => {
                        const currentUploaded = finishedBeforeBytes + uploadedBeforePart + (event.loaded || 0);
                        updateUploadProgress(currentUploaded, totalBytes, `上传 ${file.name} (${part.partNumber}/${initData.partCount || parts.length})`);
                    }
                });

                const eTag = getResponseETag(uploadResponse.headers);
                if (!eTag) {
                    throw new Error('OSS 未返回 ETag，请确认 Bucket CORS 已暴露 ETag 响应头');
                }

                uploadedFileBytes += Number(part.size || chunk.size || 0);
                completedParts.push({
                    partNumber: part.partNumber,
                    eTag
                });
                updateUploadProgress(finishedBeforeBytes + uploadedFileBytes, totalBytes, `上传 ${file.name} (${part.partNumber}/${initData.partCount || parts.length})`);
            }

            setUploadStage(`合并 ${file.name}`);
            const completeResponse = await axios.post(API_ROUTES.album_multipart_complete, {
                uploadId: initData.uploadId,
                objectKey: initData.objectKey,
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type || 'application/octet-stream',
                gameId: gameId || undefined,
                eventId: eventId || undefined,
                memberId: bindMemberId || undefined,
                tagName: getSelectedTagNames() || undefined,
                tagIds: selectedTagIds,
                remark: remark.trim() || undefined,
                parts: completedParts
            }, {
                headers: { Authorization: memberInfo?.id }
            });

            if (completeResponse.data?.code !== 200) {
                throw new Error(completeResponse.data?.msg || '合并分片上传失败');
            }

            return file.size || uploadedFileBytes;
        } catch (error) {
            await abortMultipartUpload(initData);
            throw error;
        }
    };

    const selectedFileTypes = useMemo(() => {
        const nextTypes = new Set();
        files.forEach(file => {
            if (file.type?.startsWith('video')) nextTypes.add('video');
            if (file.type?.startsWith('image')) nextTypes.add('image');
        });
        return Array.from(nextTypes);
    }, [files]);

    const currentTagMediaType = useMemo(() => {
        if (selectedFileTypes.length !== 1) return undefined;
        return selectedFileTypes[0] === 'video' ? 2 : 1;
    }, [selectedFileTypes]);

    const getTagList = useCallback(() => {
        axios.get(API_ROUTES.album_tag_list, {
            params: { mediaType: currentTagMediaType }
        }).then((response) => {
            const records = response.data?.data || [];
            setTagOptions(Array.isArray(records) ? records : []);
            // 标签类型切换或删除后，清理已经不存在的选中标签。
            setSelectedTagIds(prev => prev.filter(id => records.some(tag => String(tag.id) === String(id))));
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '标签加载失败' });
        });
    }, [currentTagMediaType]);

    useEffect(() => {
        getTagList();
    }, [getTagList]);

    const getSelectedTagNames = useCallback(() => {
        return selectedTagIds
            .map(id => tagOptions.find(tag => String(tag.id) === String(id))?.tagName)
            .filter(Boolean)
            .join(',');
    }, [selectedTagIds, tagOptions]);

    const toggleTag = (tagId) => {
        setSelectedTagIds(prev => prev.includes(tagId)
            ? prev.filter(id => id !== tagId)
            : [...prev, tagId]);
    };

    const addTag = () => {
        if (memberInfo?.admin !== 1) {
            Toast.show({ content: '只有管理员可以新增标签' });
            return;
        }
        let nextTagName = '';
        Dialog.confirm({
            title: '新增标签',
            content: (
                <Input
                    className="album-form-input"
                    placeholder="例如：世界波、合影"
                    onChange={(value) => { nextTagName = value; }}
                />
            ),
            onConfirm: async () => {
                if (!nextTagName.trim()) {
                    Toast.show({ content: '请输入标签名称' });
                    return Promise.reject();
                }
                return axios.post(API_ROUTES.album_tag_add, {
                    tagName: nextTagName.trim(),
                    mediaType: currentTagMediaType
                }, {
                    headers: { Authorization: memberInfo?.id }
                }).then((response) => {
                    if (response.data?.code === 200) {
                        const tag = response.data?.data;
                        getTagList();
                        if (tag?.id) {
                            setSelectedTagIds(prev => Array.from(new Set([...prev, tag.id])));
                        }
                    } else {
                        Toast.show({ icon: 'fail', content: response.data?.msg || '新增失败' });
                        return Promise.reject();
                    }
                }).catch(() => {
                    Toast.show({ icon: 'fail', content: '新增失败' });
                    return Promise.reject();
                });
            }
        });
    };

    const deleteTag = (tagId) => {
        if (memberInfo?.admin !== 1) {
            Toast.show({ content: '只有管理员可以删除标签' });
            return;
        }
        Dialog.confirm({
            title: '删除标签',
            content: '确认删除这个标签吗？删除后会解除已有影像的标签绑定。',
            onConfirm: () => axios.post(API_ROUTES.album_tag_delete, {
                tagId
            }, {
                headers: { Authorization: memberInfo?.id }
            }).then((response) => {
                if (response.data?.code === 200) {
                    setSelectedTagIds(prev => prev.filter(id => id !== tagId));
                    getTagList();
                    Toast.show({ icon: 'success', content: '标签已删除' });
                } else {
                    Toast.show({ icon: 'fail', content: response.data?.msg || '删除失败' });
                    return Promise.reject();
                }
            }).catch(() => {
                Toast.show({ icon: 'fail', content: '删除失败' });
                return Promise.reject();
            })
        });
    };

    const pickerConfig = useMemo(() => ({
        game: {
            title: '选择比赛场次',
            placeholder: '搜索比赛名称或时间',
            options: gameOptions,
            value: gameId,
            onConfirm: setGameId,
        },
        event: {
            title: '选择比赛事件',
            placeholder: '搜索事件、球员或时间',
            options: eventOptions,
            value: eventId,
            onConfirm: setEventId,
        },
        member: {
            title: '选择关联队员',
            placeholder: '搜索队员姓名',
            options: memberOptions,
            value: bindMemberId,
            onConfirm: setBindMemberId,
        },
    }), [bindMemberId, eventId, eventOptions, gameId, gameOptions, memberOptions]);

    const currentPicker = pickerConfig[pickerType];

    const getMemberNames = useCallback((ids) => {
        if (!ids || ids === '1') return '';
        return String(ids)
            .split(',')
            .map(id => memberOptions.find(member => String(member.value) === String(id))?.label)
            .filter(Boolean)
            .join('、');
    }, [memberOptions]);

    const getMemberIds = useCallback((ids) => {
        if (!ids || ids === '1') return [];
        return String(ids)
            .split(',')
            .map(id => id.trim())
            .filter(id => id && id !== '1');
    }, []);

    const uniqueIds = (ids) => Array.from(new Set(ids.filter(Boolean)));

    const getEventMemberId = (event) => event?.memberId || event?.member_id;
    const getEventAssistMemberId = (event) => event?.assistMemberId || event?.assist_member_id;
    const getEventUpDownMemberId = (event) => event?.upDownMemberId || event?.up_down_member_id;

    const getEventRelatedMemberIds = useCallback((event) => {
        if (!event) return [];
        // 上传接口的 memberId 支持逗号分隔；助攻人和上下场双方都自动带入。
        return uniqueIds([
            ...getMemberIds(getEventMemberId(event)),
            ...getMemberIds(getEventAssistMemberId(event)),
            ...getMemberIds(getEventUpDownMemberId(event))
        ]);
    }, [getMemberIds]);

    const getEventSearchText = useCallback((event) => {
        if (!event) return '';
        // 事件搜索只匹配事件名称和相关人员姓名，避免时间/id 干扰搜索结果。
        return [
            getEventTitle(event),
            event.userName || getMemberNames(getEventMemberId(event)),
            event.assistMemberName || getMemberNames(getEventAssistMemberId(event)),
            event.upDownName || getMemberNames(getEventUpDownMemberId(event))
        ].filter(Boolean).join(' ');
    }, [getEventTitle, getMemberNames]);

    const filteredPickerOptions = useMemo(() => {
        if (!currentPicker) return [];
        const keyword = pickerSearch.trim().toLowerCase();
        if (!keyword) return currentPicker.options;
        // 下拉数据已在前端缓存，弹窗内搜索只做本地过滤，减少重复请求。
        return currentPicker.options.filter(item => {
            if (pickerType === 'event') {
                return getEventSearchText(item.raw).toLowerCase().includes(keyword);
            }
            return String(item.searchText || item.label || '').toLowerCase().includes(keyword);
        });
    }, [currentPicker, getEventSearchText, pickerSearch, pickerType]);

    const getOptionLabel = (options, value) => {
        const target = options.find(item => item.value === value);
        return target?.label || '';
    };

    const getMultiOptionLabel = (options, value) => {
        const ids = getMemberIds(value);
        if (!ids.length) return '';
        return ids
            .map(id => options.find(item => String(item.value) === String(id))?.label)
            .filter(Boolean)
            .join('、');
    };

    const openPicker = (type) => {
        const config = pickerConfig[type];
        if (!config) return;
        setPickerType(type);
        setPickerSearch('');
        setPickerDraftValue(type === 'member' ? getMemberIds(config.value) : config.value);
    };

    const closePicker = () => {
        setPickerType('');
        setPickerSearch('');
        setPickerDraftValue('');
    };

    const confirmPicker = () => {
        if (!currentPicker) return;
        if (pickerType === 'member') {
            currentPicker.onConfirm(uniqueIds(pickerDraftValue).join(','));
            closePicker();
            return;
        }
        currentPicker.onConfirm(pickerDraftValue);
        if (pickerType === 'event' && pickerDraftValue) {
            const event = eventOptions.find(item => item.value === pickerDraftValue)?.raw;
            const relatedMemberIds = getEventRelatedMemberIds(event);
            if (relatedMemberIds.length) {
                setBindMemberId(relatedMemberIds.join(','));
            }
        }
        closePicker();
    };

    const toggleMemberDraft = (value) => {
        if (!value) {
            setPickerDraftValue([]);
            return;
        }
        setPickerDraftValue(prev => {
            const current = Array.isArray(prev) ? prev : getMemberIds(prev);
            return current.includes(value)
                ? current.filter(id => id !== value)
                : [...current, value];
        });
    };

    const isOptionSelected = (value) => {
        if (pickerType === 'member') {
            return Array.isArray(pickerDraftValue) && pickerDraftValue.includes(value);
        }
        return pickerDraftValue === value;
    };

    const renderEventOptionContent = (item) => {
        const event = item.raw;
        if (!event) return <span>{item.label}</span>;

        const kind = getEventKind(event);
        const showTime = hasEventTime(event);
        const timeText = showTime ? (event.eventTimeName || formatEventTime(event.eventTime)) : '';
        const teamText = event.teamName || '';
        const playerText = event.userName || getMemberNames(getEventMemberId(event));
        const assistName = event.assistMemberName || getMemberNames(getEventAssistMemberId(event));
        const assistText = assistName ? `助攻 ${assistName}` : '';
        // 上下场事件：member_id 是上场人，up_down_member_id 是下场人。
        const upNames = event.userName || getMemberNames(getEventMemberId(event));
        const downNames = event.upDownName || getMemberNames(getEventUpDownMemberId(event));
        const fallbackText = event.currentTime || '比赛节点';
        const metaItems = kind === 'sub'
            ? [
                upNames ? `上场 ${upNames}` : '',
                downNames ? `下场 ${downNames}` : ''
            ].filter(Boolean)
            : [playerText, assistText].filter(Boolean);

        return (
            <div className={`album-event-option-card album-event-option-card--${kind}`}>
                {showTime ? <div className="album-event-time-pill">{timeText}</div> : <div className="album-event-time-spacer" />}
                <div className="album-event-card-body">
                    <div className="album-event-title-row">
                        <span className="album-event-section-badge">第{event.sectionIndex || 1}节</span>
                        <span className="album-event-kind">{getEventTitle(event)}</span>
                        {teamText && <span className="album-event-team">{teamText}</span>}
                    </div>
                    <div className="album-event-meta-row">
                        {metaItems.length > 0 ? metaItems.map(text => <span key={text}>{text}</span>) : <span>{fallbackText}</span>}
                    </div>
                </div>
            </div>
        );
    };

    const renderPickerOptionContent = (item) => {
        if (pickerType === 'event') {
            return renderEventOptionContent(item);
        }
        if (pickerType === 'member') {
            return renderMemberOptionContent(item);
        }
        return <span>{item.label}</span>;
    };

    const renderMemberOptionContent = (item) => (
        <div className="album-member-option-content">
            <strong className="album-member-option-name">{item.label}</strong>
            {item.typeLabel && (
                <em className={`album-member-type-badge album-member-type-${item.typeOrder}`}>
                    {item.typeLabel}
                </em>
            )}
        </div>
    );

    const submitUpload = async () => {
        if (!files.length) {
            Toast.show({ content: '请选择图片或视频' });
            return;
        }
        if (!remark.trim()) {
            Toast.show({ content: '请输入图片/视频名称' });
            return;
        }

        const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
        setUploading(true);
        updateUploadProgress(0, totalBytes, '准备上传');
        Toast.show({ icon: 'loading', content: '上传中...', duration: 0 });

        try {
            let finishedBytes = 0;
            for (const file of files) {
                const uploadedSize = await uploadFileByMultipart(file, finishedBytes, totalBytes);
                finishedBytes += uploadedSize;
                updateUploadProgress(finishedBytes, totalBytes, `已完成 ${file.name}`);
            }

            updateUploadProgress(totalBytes, totalBytes, '上传完成');
            Toast.show({ icon: 'success', content: memberInfo?.admin === 1 ? '上传成功' : '上传成功，待审核' });
            if (fixedGameId) {
                navigate('/album', { state: { gameId: fixedGameId, gameName: fixedGameName } });
            } else {
                navigate('/album');
            }
        } catch (error) {
            const isNetworkCorsError = error?.isAxiosError && !error.response;
            Toast.show({
                icon: 'fail',
                content: isNetworkCorsError
                    ? 'OSS 直传被浏览器跨域拦截，请检查 Bucket CORS 配置'
                    : (error?.message || '上传失败')
            });
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setUploadLoadedBytes(0);
            setUploadStage('');
        }
    };

    return (
        <div className="album-page album-upload-page">
            <NavBar className="album-navbar" onBack={() => navigate(-1)}>
                上传影像
            </NavBar>

            <div className="album-hero album-upload-hero">
                <div>
                    <div className="album-hero-kicker">UPLOAD</div>
                    <div className="album-hero-title">上传球队影像</div>
                    <div className="album-hero-desc">可以绑定比赛、赛况事件、队员，也可以只作为球队相册记录。</div>
                </div>
                <div className="album-hero-icon">⬆️</div>
            </div>

            <div className="album-upload-form">
                <div className="album-form-section-title">上传文件</div>
                <div className="album-file-picker" onClick={() => fileInputRef.current?.click()}>
                    <div className="album-file-picker-icon">📷</div>
                    <div>
                        <div className="album-file-picker-title">选择图片或视频</div>
                        <div className="album-file-picker-desc">{files.length ? `已选择 ${files.length} 个文件` : '支持多图片、多视频一次上传'}</div>
                    </div>
                </div>
                <input
                    ref={fileInputRef}
                    className="album-file-input"
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleChooseFiles}
                />

                {files.length > 0 && (
                    <div className="album-selected-files">
                        {files.map((file, index) => (
                            <div key={`${file.name}-${index}`} className="album-selected-file">
                                <span>{file.name}</span>
                                <span>{file.type?.startsWith('video') ? '视频' : '图片'}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className="album-field-label">展示名称 <em className="album-required">*</em></div>
                <Input className="album-form-input" placeholder="图片/视频名称，必填" value={remark} onChange={setRemark} />

                <div className="album-form-section-title">绑定信息</div>
                {fixedGameId ? (
                    <div className="album-fixed-game-card">
                        <div className="album-fixed-game-label">当前绑定比赛</div>
                        <div className="album-fixed-game-name">{fixedGameName || getOptionLabel(gameOptions, gameId) || '当前比赛'}</div>
                    </div>
                ) : (
                    <>
                        <div className="album-field-label">比赛场次</div>
                        <button className="album-picker-field" type="button" onClick={() => openPicker('game')}>
                            <span>{getOptionLabel(gameOptions, gameId) || '不绑定比赛'}</span>
                            <i>选择</i>
                        </button>
                    </>
                )}

                <div className="album-field-label">比赛事件</div>
                <button className="album-picker-field" type="button" onClick={() => openPicker('event')}>
                    <span>{getOptionLabel(eventOptions, eventId) || '不绑定事件'}</span>
                    <i>选择</i>
                </button>

                <div className="album-field-label">关联队员</div>
                <button className="album-picker-field" type="button" onClick={() => openPicker('member')}>
                    <span>{getMultiOptionLabel(memberOptions, bindMemberId) || '不绑定队员'}</span>
                    <i>选择</i>
                </button>

                <div className="album-field-label">选择标签</div>
                <div className="album-tag-picker">
                    {/* 标签直接铺开，管理员在最后通过按钮维护标签库 */}
                    {tagOptions.length > 0 || memberInfo?.admin === 1 ? (
                        <div className="album-tag-picker-options">
                            {tagOptions.map(tag => (
                                <span key={tag.id} className={`album-upload-tag-shell ${selectedTagIds.includes(tag.id) ? 'active' : ''}`}>
                                    <button
                                        type="button"
                                        className="album-upload-tag-option"
                                        onClick={() => toggleTag(tag.id)}
                                    >
                                        {tag.tagName}
                                    </button>
                                    {memberInfo?.admin === 1 && (
                                        <button type="button" className="album-upload-tag-delete" onClick={() => deleteTag(tag.id)}>
                                            ×
                                        </button>
                                    )}
                                </span>
                            ))}
                            {memberInfo?.admin === 1 && (
                                <button type="button" className="album-tag-add-chip" onClick={addTag}>
                                    + 增加标签
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="album-tag-picker-empty">暂无可选标签</div>
                    )}
                </div>

                {uploading && (
                    <div className="album-upload-progress">
                        <div className="album-upload-progress-head">
                            <span>{uploadStage || '正在上传影像'}</span>
                            <strong>{uploadProgress}%</strong>
                        </div>
                        <div className="album-upload-progress-size">
                            {formatFileSize(uploadLoadedBytes)} / {formatFileSize(uploadTotalBytes)}
                        </div>
                        <ProgressBar percent={uploadProgress} />
                    </div>
                )}

                <Button block color="primary" loading={uploading} className="album-submit-btn" onClick={submitUpload}>
                    提交上传
                </Button>
            </div>

            <Popup
                visible={!!currentPicker}
                onMaskClick={closePicker}
                bodyStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
            >
                {currentPicker && (
                    <div className="album-picker-popup">
                        <div className="album-picker-head">
                            <button type="button" onClick={closePicker}>取消</button>
                            <strong>{currentPicker.title}</strong>
                            <button type="button" onClick={confirmPicker}>确认</button>
                        </div>
                        <SearchBar
                            className="album-picker-search"
                            placeholder={currentPicker.placeholder}
                            value={pickerSearch}
                            onChange={setPickerSearch}
                        />
                        <div className={`album-picker-list ${pickerType === 'member' ? 'album-member-picker-grid' : ''}`}>
                            {filteredPickerOptions.length > 0 ? filteredPickerOptions.map(item => (
                                <button
                                    key={item.value}
                                    type="button"
                                    className={`album-picker-option ${pickerType === 'event' ? 'album-picker-option--event' : ''} ${isOptionSelected(item.value) ? 'active' : ''}`}
                                    onClick={() => pickerType === 'member' ? toggleMemberDraft(item.value) : setPickerDraftValue(item.value)}
                                >
                                    {renderPickerOptionContent(item)}
                                    <i>{isOptionSelected(item.value) ? '已选' : ''}</i>
                                </button>
                            )) : (
                                <Empty description="没有匹配的数据" imageStyle={{ width: 80 }} />
                            )}
                        </div>
                    </div>
                )}
            </Popup>
        </div>
    );
}
